import { useCallback, useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { RouteStop } from "@/reducers/routes2gSlice"
import { RouteInfo } from "@/components/maps/RouteView"
import { v4 as uuidv4 } from "uuid"
import AdHocCommands, { AvoidType, LoadType } from "@/api/adHocCommands"
import PlanOrderOnPage from "@/components/analyzeOrderOnPage/PlanOrderOnPage"
import { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"
import { XmppContext } from "@/stream/xmppClient"
import PlanRouteParser, { AlternativeRoute } from "@/lib/planRouteParser"
import { useDetailedReverseGeocoder } from "@/hooks/useReverseGeocode"
import type { PlanRouteSnapshot } from "@/components/analyzeOrderOnPage/planRouteTabs"

interface PlanOrderOnPageModalProps {
  isOpen: boolean
  onClose: () => void
  sharedRouteState?: any
  onSwitchToAnalyzeOrder?: ({
    avoid,
    loadType,
    vehicleType,
    selectedDriver,
    file,
  }: {
    avoid: AvoidType[]
    loadType: LoadType[]
    vehicleType: VehicleType
    selectedDriver: SelectedDriverLocation | undefined
    file?: File
  }) => void
  onSwitchToTextOrder?: ({
    avoid,
    loadType,
    vehicleType,
    selectedDriver,
  }: {
    avoid: AvoidType[]
    loadType: LoadType[]
    vehicleType: VehicleType
    selectedDriver: SelectedDriverLocation | undefined
  }) => void
  onLoadSavedRoute?: (routeId: string) => void
  sessionId?: string | undefined
  onReset?: () => void
  onRouteModified?: (stopCount: number) => void
  tabBar?: React.ReactNode
  onStateChange?: (snapshot: PlanRouteSnapshot) => void
  routeName?: string
}

export default function PlaneOrderOnPageModal({
  isOpen,
  onClose,
  sharedRouteState,
  onSwitchToAnalyzeOrder,
  onSwitchToTextOrder,
  onLoadSavedRoute,
  sessionId,
  onReset,
  onRouteModified,
  tabBar,
  onStateChange,
  routeName,
}: PlanOrderOnPageModalProps) {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const reverseGeocoder = useDetailedReverseGeocoder()
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const fetchPolylines = useCallback(
    async (
      stops: RouteStop[],
      avoidOptions: AvoidType[],
      loadType: LoadType[],
      carConfig?: any,
    ): Promise<
      | [RouteInfo | undefined, RouteStop[], AlternativeRoute[] | undefined]
      | undefined
    > => {
      if (!client || !myJid) {
        console.warn("XMPP client not available")
        return [undefined, stops, undefined]
      }

      // Prepare stops with addressUpdated flag for XMPP processing
      const stopsForXmpp = stops.map((stop) => ({
        ...stop,
        id: stop.id ?? uuidv4(),
        route_handle: undefined,
        addressUpdated: true,
        is_temporary: true,
      }))
      if (stopsForXmpp.length > 0) {
        stopsForXmpp[0].remaining_time = 0
        stopsForXmpp[0].remaining_distance = 0
      }

      if (stopsForXmpp.length < 2) {
        const firstStop = stopsForXmpp[0]
        const [lat, lon] =
          firstStop?.location.formattedAddress?.split(",") ?? ","

        console.log("[DEBUG] First stop ", lat, lon)
        const location = await reverseGeocoder(lat, lon)

        stopsForXmpp[0] = {
          ...stopsForXmpp[0],
          action: stopsForXmpp[0].action ?? "waypoint",
          id: stopsForXmpp[0].id ?? uuidv4(),
          is_active: true,
          is_temporary: true,
          location: {
            ...location,
          },
        }

        return [undefined, stopsForXmpp, undefined]
      }

      try {
        console.debug(
          "[DEBUG] Fetching order polylines via XMPP with avoid:",
          avoidOptions,
          "and loadType:",
          loadType,
          "car config:",
          carConfig,
          "stops:",
          stopsForXmpp,
        )

        const result = await AdHocCommands.getRoutePlan(
          client,
          myJid.toString(),
          stopsForXmpp,
          {
            ...carConfig,
            avoid: avoidOptions,
            loadType: loadType,
          },
        )

        const parsed = PlanRouteParser.parse(result)

        if (parsed?.route && parsed?.stops) {
          console.log("[DEBUG] ", parsed.stops)
          return [
            parsed.route,
            [stopsForXmpp[0], ...parsed.stops] as RouteStop[],
            parsed.alternativeRoutes,
          ]
        }

        console.warn("Failed to parse route data from XMPP response")
        return [undefined, stopsForXmpp, undefined]
      } catch (err) {
        console.warn("Route calculation error:", err)
        window.mapToaster?.show({
          message: t("planOrder.routeToasts.calculateFailed"),
          intent: "danger",
          timeout: 3000,
        })
        return [undefined, stopsForXmpp, undefined]
      }
    },
    [client, myJid, reverseGeocoder, t],
  )

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center rounded-md bg-black/70 bg-opacity-50">
      <div className="relative h-full w-full bg-white">
        {/* X button is rendered inside PlanOrderOnPage to ensure save before close */}
        <div className="h-full w-full">
          <PlanOrderOnPage
            onFetchPolylines={fetchPolylines}
            onClose={handleClose}
            sharedRouteState={sharedRouteState}
            onSwitchToAnalyzeOrder={onSwitchToAnalyzeOrder}
            onSwitchToTextOrder={onSwitchToTextOrder}
            onLoadSavedRoute={onLoadSavedRoute}
            sessionId={sessionId}
            onReset={onReset}
            onRouteModified={onRouteModified}
            tabBar={tabBar}
            onStateChange={onStateChange}
            routeName={routeName}
          />
        </div>
      </div>
    </div>
  )
}
